import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DialogContent, TextField, Typography, Button, DialogActions } from '@material-ui/core';
import { Form, Formik } from 'formik';

// UTILITY
import setYupValidationSchema from '@/utility/validationSchema/validationSchema';
import { analyticsReportModalShown } from '@/utility/analytics';

// COMPONENTS
import Loader from '@/components/Loader';

// STYLES
import { DialogStyled, CloseIcon } from './styles';

const DialogReport = ({ data, onClose, onSubmit, analyticsLocation, dispatch, ...props }) => {
  const [t] = useTranslation();
  const [wasOpened, setWasOpened] = useState(false);

  const { loading } = useSelector(({ app }) => ({
    loading: app.loadings.feedback,
  }));

  function handleSubmit(values, { setSubmitting }) {
    onSubmit({ ...data, ...values }, () => {
      onClose();
    });
    setSubmitting();
  }

  function getContentTitle() {
    if (!data.type && !data.content_title) return null;

    return (
      <Typography className="m-b-20 p4">
        {t(`common.${data.contentType}`)}:{' '}
        <Typography variant="h6" component="span">
          {data.content_title}
        </Typography>
      </Typography>
    );
  }

  function handleDialogOpen() {
    if (!wasOpened) {
      analyticsReportModalShown({
        ...data,
        location: analyticsLocation,
      });

      setWasOpened(true);
    }
  }

  return (
    <DialogStyled
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      TransitionProps={{ onEntered: handleDialogOpen }}
      {...props}
    >
      <Formik
        initialValues={{ feedback: '' }}
        validationSchema={setYupValidationSchema('reportSchema', t)}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnMount
      >
        {({ values, touched, errors, handleChange, handleBlur, isValid, isSubmitting }) => (
          <Form>
            <DialogContent>
              <CloseIcon size="medium" aria-label="close" onClick={onClose}>
                <i className="icon-close" />
              </CloseIcon>
              <Typography className="m-b-30" variant="h3" align="center">
                {t('reportDialog.title')}
              </Typography>
              {getContentTitle()}
              <Typography className="p4 m-b-30">{t('reportDialog.subtitle')}</Typography>
              <Typography className="m-b-15" variant="h5">
                {t('inputs.comment')}
              </Typography>
              <TextField
                className="m-b-25"
                name="feedback"
                value={values.feedback}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.feedback && !!errors.feedback}
                helperText={touched.feedback && errors.feedback}
                multiline
                minRows={5}
                maxRows={5}
              />
              <DialogActions>
                <Button type="submit" color="primary" disabled={!isValid || isSubmitting}>
                  {t('buttons.report')}
                </Button>
              </DialogActions>
            </DialogContent>
          </Form>
        )}
      </Formik>
      <Loader active={loading} />
    </DialogStyled>
  );
};

DialogReport.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  analyticsLocation: PropTypes.string,
  dispatch: PropTypes.func,
};

export default React.memo(DialogReport);
